import React from 'react';
import { Button, Card, message, Modal, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import {
  McpAdminTokenDto,
  apiAdminMcpTokensList,
  apiAdminMcpTokenRevoke,
} from '../../services/api/mcp';

interface DataType extends McpAdminTokenDto {
  key: string;
}

const PER_PAGE_LIMIT = 20;

function McpTokensPage() {
  const [dataSource, setDataSource] = React.useState<
    [DataType[], number] | null
  >(null);
  const [paging, setPaging] = React.useState<{ offset: number; limit: number }>(
    { offset: 0, limit: PER_PAGE_LIMIT },
  );
  const [loading, setLoading] = React.useState(false);
  const { token } = useSelector(selectUserState);

  const loadTokens = React.useCallback(
    async (offset: number, limit: number) => {
      if (!token) return;
      setLoading(true);
      const response = await apiAdminMcpTokensList({ offset, limit }, token);
      if (response) {
        setDataSource([
          response.tokens.map((t) => ({ ...t, key: t.aliasId })),
          response.total,
        ]);
      } else {
        setDataSource([[], 0]);
      }
      setLoading(false);
    },
    [token],
  );

  React.useEffect(() => {
    loadTokens(paging.offset, paging.limit);
  }, [paging]);

  const handleRevoke = React.useCallback(
    (record: DataType) => {
      Modal.confirm({
        title: 'Revoke MCP Token',
        content: `Are you sure you want to revoke token "${record.aliasId}" for ${record.userEmail}?`,
        okText: 'Revoke',
        okButtonProps: { danger: true },
        onOk: async () => {
          if (!token) return;
          const success = await apiAdminMcpTokenRevoke(record.aliasId, token);
          if (success) {
            message.success('Token revoked successfully');
            loadTokens(paging.offset, paging.limit);
          } else {
            message.error('Failed to revoke token');
          }
        },
      });
    },
    [token, paging, loadTokens],
  );

  const columns = React.useMemo<ColumnsType<DataType>>(
    () => [
      {
        key: 'aliasId',
        title: 'Alias ID',
        dataIndex: 'aliasId',
        render: (v) => `${v}`,
      },
      {
        key: 'userEmail',
        title: 'User Email',
        dataIndex: 'userEmail',
        render: (v) => `${v}`,
      },
      {
        key: 'userId',
        title: 'User ID',
        dataIndex: 'userId',
        render: (v) => `${v}`,
      },
      {
        key: 'clientName',
        title: 'Client Name',
        dataIndex: 'clientName',
        render: (v) => `${v}`,
      },
      {
        key: 'scopes',
        title: 'Scopes',
        dataIndex: 'scopes',
        render: (v: string[]) => v.join(', '),
      },
      {
        key: 'createdAt',
        title: 'Created At',
        dataIndex: 'createdAt',
        render: (v) => `${v}`,
      },
      {
        key: 'lastUsedAt',
        title: 'Last Used At',
        dataIndex: 'lastUsedAt',
        render: (v) => (v ? `${v}` : '—'),
      },
      {
        key: 'expiresAt',
        title: 'Expires At',
        dataIndex: 'expiresAt',
        render: (v) => `${v}`,
      },
      {
        key: 'actions',
        title: 'Actions',
        render: (_, record) => (
          <Button danger onClick={() => handleRevoke(record)}>
            Revoke
          </Button>
        ),
      },
    ],
    [handleRevoke],
  );

  return (
    <Card bordered={false} title="MCP Tokens">
      {dataSource ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <strong>Total:</strong> {dataSource[1]}
          </div>
          <Table
            loading={loading}
            scroll={{ x: true }}
            dataSource={dataSource[0]}
            columns={columns}
            locale={{ emptyText: 'No tokens found' }}
            pagination={{
              total: dataSource[1],
              pageSize: PER_PAGE_LIMIT,
              current: paging.offset / paging.limit + 1,
            }}
            onChange={(pagination) => {
              if (pagination.current) {
                const size = pagination.pageSize || PER_PAGE_LIMIT;
                const offset = size * (pagination.current - 1);
                setPaging({ offset, limit: size });
              }
            }}
          />
        </>
      ) : null}
    </Card>
  );
}

export default McpTokensPage;
